import { useEffect, useState } from "react";
import { BrowserRouter, NavLink, Navigate, Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createUserByAdmin, login } from "./services/authService";
import { LOGOUT_EVENT } from "./services/http";
import CategoriesPage from "./pages/CategoriesPage";
import EmailPage from "./pages/EmailPage";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import StatsPage from "./pages/StatsPage";
import WelcomePage from "./pages/WelcomePage";
import "./styles.css";

export default function App() {
  const { t, i18n } = useTranslation();
  const [authUser, setAuthUser] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("authUser") || "null");
      // Sessions saved before tokens existed cannot call the API: log in again.
      return stored?.token ? stored : null;
    } catch {
      return null;
    }
  });

  // An API call answered 401 (expired session): return to the login page.
  useEffect(() => {
    const onLogout = () => setAuthUser(null);
    window.addEventListener(LOGOUT_EVENT, onLogout);
    return () => window.removeEventListener(LOGOUT_EVENT, onLogout);
  }, []);

  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminRole, setAdminRole] = useState("USER");

  async function handleLogin(credentials) {
    const result = await login(credentials);
    const session = { ...result.user, token: result.token };
    localStorage.setItem("authUser", JSON.stringify(session));
    setAuthUser(session);
  }

  function handleLogout() {
    localStorage.removeItem("authUser");
    setAuthUser(null);
  }

  function toggleLanguage() {
    const newLang = i18n.language === "fr" ? "en" : "fr";
    i18n.changeLanguage(newLang);
    localStorage.setItem("language", newLang);
  }

  async function handleAdminCreateUser(e) {
    e.preventDefault();

    if (!authUser?.id) return;

    await createUserByAdmin({
      email: adminEmail,
      password: adminPassword,
      role: adminRole
    });

    setAdminEmail("");
    setAdminPassword("");
    setAdminRole("USER");
    alert("User created");
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <div className={`app-shell ${authUser ? "" : "authless"}`}>
        {authUser ? (
          <aside className="sidebar">
            <h1>{t('app.title')}</h1>
            <nav>
              <NavLink to="/home" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>{t('nav.home')}</NavLink>
              <NavLink to="/stats" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>{t('nav.stats')}</NavLink>
              <NavLink to="/categories" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>{t('nav.categories')}</NavLink>
              <NavLink to="/emails" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>{t('nav.emails')}</NavLink>
              <NavLink to="/welcome" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>{t('nav.welcome')}</NavLink>
            </nav>

            {authUser.role === "ADMIN" ? (
              <form className="admin-user-form" onSubmit={handleAdminCreateUser}>
                <h4>{t('admin.addUser')}</h4>
                <input
                  type="email"
                  placeholder={t('admin.userEmail')}
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  required
                />
                <input
                  type="password"
                  placeholder={t('admin.userPassword')}
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  required
                />
                <select value={adminRole} onChange={(e) => setAdminRole(e.target.value)}>
                  <option value="USER">{t('admin.user')}</option>
                  <option value="ADMIN">{t('admin.admin')}</option>
                </select>
                <button type="submit">{t('admin.createUser')}</button>
              </form>
            ) : null}

            <div className="sidebar-footer">
              <span className="role-badge">{t('admin.roleLabel')}: {authUser.role}</span>
              <button type="button" className="logout-btn" onClick={handleLogout}>{t('nav.logout')}</button>
              <button type="button" className="lang-btn" onClick={toggleLanguage}>
                {i18n.language === "fr" ? "EN" : "FR"}
              </button>
            </div>
          </aside>
        ) : null}

        <main className="main-content">
          <Routes>
            <Route path="/login" element={authUser ? <Navigate to="/emails" replace /> : <LoginPage onLogin={handleLogin} />} />
            <Route path="/" element={<Navigate to={authUser ? "/emails" : "/login"} replace />} />
            <Route path="/welcome" element={authUser ? <WelcomePage /> : <Navigate to="/login" replace />} />
            <Route path="/home" element={authUser ? <HomePage /> : <Navigate to="/login" replace />} />
            <Route path="/stats" element={authUser ? <StatsPage /> : <Navigate to="/login" replace />} />
            <Route path="/categories" element={authUser ? <CategoriesPage /> : <Navigate to="/login" replace />} />
            <Route path="/emails" element={authUser ? <EmailPage /> : <Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to={authUser ? "/emails" : "/login"} replace />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  );
}